import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Plus } from 'lucide-react';

interface PurchaseOrder {
  id: string;
  poNumber: string;
  customerName: string;
  partnerName?: string;
  totalAmount: number;
  status: string;
  createdAt: number;
  approvedAt?: number;
}

function PurchaseOrders() {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [newPO, setNewPO] = useState({
    poNumber: '',
    customer: '',
    partner: '',
    amount: 0,
    status: 'PENDING',
  });

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/purchase-orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPurchaseOrders(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-gray-500',
      SUBMITTED: 'bg-blue-500',
      APPROVED: 'bg-green-500',
      REJECTED: 'bg-red-500',
      COMPLETED: 'bg-purple-500',
      CANCELLED: 'bg-orange-500',
      PENDING: 'bg-yellow-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const handleCreatePO = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newPO),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewPO({
          poNumber: '',
          customer: '',
          partner: '',
          amount: 0,
          status: 'PENDING',
        });
        fetchPurchaseOrders();
      }
    } catch (error) {
      console.error('Failed to create purchase order:', error);
    }
  };

  const openViewDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsViewDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Purchase Orders
          </h2>
          <p className="text-muted-foreground mt-2">
            Manage purchase orders for enterprise customers
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create PO
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Purchase Orders</CardTitle>
          <CardDescription>
            {purchaseOrders.length} purchase order{purchaseOrders.length !== 1 ? 's' : ''} total
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO Number</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchaseOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No purchase orders found
                  </TableCell>
                </TableRow>
              ) : (
                purchaseOrders.map((po) => (
                  <TableRow key={po.id}>
                    <TableCell className="font-medium">{po.poNumber}</TableCell>
                    <TableCell>{po.customerName}</TableCell>
                    <TableCell>{po.partnerName || '-'}</TableCell>
                    <TableCell>${po.totalAmount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(po.status)}>
                        {po.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(po.createdAt * 1000).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openViewDialog(po)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Purchase Order Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
            <DialogDescription>
              Create a new purchase order
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="poNumber">PO Number</Label>
              <Input
                id="poNumber"
                value={newPO.poNumber}
                onChange={(e) => setNewPO({ ...newPO, poNumber: e.target.value })}
                placeholder="PO-2024-001"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="customer">Customer</Label>
              <Input
                id="customer"
                value={newPO.customer}
                onChange={(e) => setNewPO({ ...newPO, customer: e.target.value })}
                placeholder="Customer Name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="partner">Partner (Optional)</Label>
              <Input
                id="partner"
                value={newPO.partner}
                onChange={(e) => setNewPO({ ...newPO, partner: e.target.value })}
                placeholder="Partner Name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount ($)</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={newPO.amount}
                onChange={(e) => setNewPO({ ...newPO, amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newPO.status}
                onValueChange={(value) => setNewPO({ ...newPO, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePO} disabled={!newPO.poNumber || !newPO.customer}>
              Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Purchase Order Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
            <DialogDescription>
              View purchase order information
            </DialogDescription>
          </DialogHeader>
          {selectedPO && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>PO Number</Label>
                <div className="text-sm font-medium">{selectedPO.poNumber}</div>
              </div>
              <div className="grid gap-2">
                <Label>Customer</Label>
                <div className="text-sm">{selectedPO.customerName}</div>
              </div>
              <div className="grid gap-2">
                <Label>Partner</Label>
                <div className="text-sm">{selectedPO.partnerName || '-'}</div>
              </div>
              <div className="grid gap-2">
                <Label>Amount</Label>
                <div className="text-sm">${selectedPO.totalAmount.toFixed(2)}</div>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <div>
                  <Badge className={getStatusBadgeColor(selectedPO.status)}>
                    {selectedPO.status}
                  </Badge>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Created</Label>
                <div className="text-sm">
                  {new Date(selectedPO.createdAt * 1000).toLocaleString()}
                </div>
              </div>
              {selectedPO.approvedAt && (
                <div className="grid gap-2">
                  <Label>Approved</Label>
                  <div className="text-sm">
                    {new Date(selectedPO.approvedAt * 1000).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PurchaseOrders;
